import {
  Alert,
  AlertTitle,
  Box,
  IconButton,
  Skeleton,
  Typography,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  RadialLinearScale,
  PolarAreaController,
  RadarController,
} from "chart.js";
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2";
import TableSkeleton from "../../common/components/TableSkeleton";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import CustomProgressLine from "../../rbm/components/CustomProgressLine";
import DownloadIcon from "@mui/icons-material/Download";
import { Radar, PolarArea } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  RadarController,
  PolarAreaController,
  RadialLinearScale,
  Title,
  Tooltip,
  Filler,
  Legend,
  annotationPlugin,
  ChartDataLabels,
  zoomPlugin
);

ChartJS.defaults.animation = {
  duration: 1200,
  easing: "easeInOutQuad",
  delay: 100,
  loop: false,
};

const chartTypeMap = {
  bar: Bar,
  line: Line,
  pie: Pie,
  doughnut: Doughnut,
};

// Centralized plugin to draw center total or percentage
const getCenterTextPlugin = (displayType = "none") => ({
  id: "centerText",
  beforeDraw(chart) {
    if (!chart?.data?.datasets?.[0]?.data?.length) return;
    const { ctx, width, height } = chart;
    const data = chart.data.datasets[0].data;
    const total = data.reduce((acc, val) => acc + parseInt(val), 0);
    let centerText = "";

    if (displayType === "total") {
      centerText = `Total: ${total.toFixed(1)}`;
    } else if (displayType === "percentage") {
      const percent = ((+data[0] / total) * 100).toFixed(1);
      centerText = `${percent}%`;
    } else return;

    ctx.save();
    ctx.font = "bold 18px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#333";
    ctx.fillText(centerText, width / 2, height / 2);
    ctx.restore();
  },
});

export const CustomizedChart = ({
  type = "bar",
  data,
  title = "",
  subtitle = "",
  loading = false,
  showDownload = true,
  enableDataLabels = false,
  centerDisplay = "none", // "total" | "percentage" | "none"
  enableAnnotations = false,
  annotationConfig = {},
  chartOptions = {},
  showTypeToggle = false,
  tooltipFormatter,
  enableZoom = true,
}) => {
  const chartRef = useRef(null);
  const [chartType, setChartType] = useState(type);
  const ChartComponent = chartTypeMap[chartType];

  const handleDownload = () => {
    const chart = chartRef.current;
    if (!chart) return;

    const canvas = chart.canvas;
    const ctx = canvas.getContext("2d");

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d");

    tempCtx.fillStyle = "#fff";
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(canvas, 0, 0);

    const url = tempCanvas.toDataURL();
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title || "chart"}.png`;
    a.click();
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: !!title,
        text: title,
      },
      datalabels: enableDataLabels
        ? {
            display: (context) => {
              // Hide label if value is zero
              const value = context.dataset.data[context.dataIndex];
              return value !== 0;
            },
            color: "#000",
            anchor: "end",
            align: "top",
            font: {
              weight: "bold",
            },
            formatter: (value) => parseInt(value).toFixed(0),
          }
        : false,
      tooltip: tooltipFormatter
        ? {
            callbacks: {
              label: tooltipFormatter,
            },
          }
        : {},
      annotation:
        enableAnnotations && chartType !== "pie" ? annotationConfig : undefined,
      zoom: enableZoom
        ? {
            pan: { enabled: true, mode: "xy" },
            zoom: {
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: "xy",
            },
          }
        : undefined,
      ...chartOptions.plugins,
    },
    ...chartOptions,
  };

  const plugins = [
    ...(chartType === "doughnut" && centerDisplay !== "none"
      ? [getCenterTextPlugin(centerDisplay)]
      : []),
  ];

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
        backgroundColor: "#fff",
        position: "relative",
      }}
      role="region"
      aria-label={`Chart: ${title}`}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          {showTypeToggle && (
            <ToggleButtonGroup
              size="small"
              value={chartType}
              exclusive
              onChange={(e, val) => val && setChartType(val)}
              aria-label="chart type toggle"
            >
              <ToggleButton value="bar">Bar</ToggleButton>
              <ToggleButton value="line">Line</ToggleButton>
              <ToggleButton value="pie">Pie</ToggleButton>
              <ToggleButton value="doughnut">Doughnut</ToggleButton>
            </ToggleButtonGroup>
          )}
          {showDownload && (
            <MuiTooltip title="Download chart as PNG">
              <IconButton onClick={handleDownload} size="small">
                <DownloadIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 300 }}>
        <ChartComponent
          key={chartType} // 🛠️ ensures chart updates when type changes
          ref={chartRef}
          data={data}
          options={options}
          plugins={plugins}
          aria-hidden={loading}
          role="img"
        />
      </Box>
    </Box>
  );
};

CustomizedChart.propTypes = {
  type: PropTypes.oneOf(["bar", "line", "pie", "doughnut"]),
  data: PropTypes.object.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
  showDownload: PropTypes.bool,
  enableDataLabels: PropTypes.bool,
  centerDisplay: PropTypes.oneOf(["none", "total", "percentage"]),
  enableAnnotations: PropTypes.bool,
  annotationConfig: PropTypes.object,
  chartOptions: PropTypes.object,
  showTypeToggle: PropTypes.bool,
  tooltipFormatter: PropTypes.func,
  enableZoom: PropTypes.bool,
};

export const DashboardLabel = ({
  labelTitle = "Title",
  labelHint = "",
  labelValue,
  valueType = "normal",
  showProgressBar = false,
  loading = false,
}) => {
  const _value = Number(
    labelValue !== null && labelValue !== undefined && !isNaN(+labelValue)
      ? +labelValue
      : 0
  );

  return (
    <div
      style={{ color: "#ffe6aa" }}
      className="bg-info border-dashed border-gray-300 border-dashed rounded min-w-80px py-2 px-2 mx-2 mb-1 mt-1"
      title={labelHint || labelTitle}
    >
      <div className="fw-semibold font-weight-bold text-center">
        {labelTitle}
      </div>
      <div
        style={{ color: "#9cff71" }}
        className="fs-6 font-weight-bolder text-center"
      >
        {loading ? (
          <Skeleton variant="text" sx={{ bgcolor: "grey.400" }} />
        ) : valueType === "percentage" ? (
          `${+_value.toFixed(2)}%`
        ) : (
          _value
        )}
      </div>
      {valueType === "percentage" && showProgressBar ? (
        loading ? (
          <Skeleton variant="text" sx={{ bgcolor: "grey.400", height: 6 }} />
        ) : (
          <CustomProgressLine value={_value} hidePercentage />
        )
      ) : null}
    </div>
  );
};

DashboardLabel.propTypes = {
  labelTitle: PropTypes.string,
  labelValue: PropTypes.number,
};

/**
 * @props {*} labelPosition 
 * @labelPosition :"inside" | "outside" | "auto"
 * @inside	Label will be centered inside the pie slice
 * @outside	Label will be pushed outside like your image
 * @auto	Library will smartly decide: inside if enough space, outside if too small
 */

export const DashboardPieChart = ({
  pieTitle = "Employee by gender",
  pieData,
  loading = false,
  enableDataLabels = false,
  isForMoney = false,
  useShortNumber = false,
  showPercentage = false,
  labelSize = 12, // new optional control
  labelPosition = "inside", // inside | outside | auto
  labelOffset, // distance from the chart
}) => {
  const isLabelOutside = labelPosition === "outside" || !!showPercentage;
  return (
    <>
      {!loading && !!pieData?.labels ? (
        <div
          className="border rounded p-2 dashboard-card"
          role="region"
          aria-label={`Pie chart: ${pieTitle}`}
        >
          <Pie
            role="img"
            redraw={true}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "bottom",
                },
                title: {
                  display: true,
                  text: pieTitle,
                },
                datalabels: enableDataLabels
                  ? {
                      display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
                      anchor: !isLabelOutside ? "center" : "end",
                      // align: !isLabelOutside ? "center" : "end",
                      clamp: labelPosition === "auto" ? true : false,
                      // clip: !isLabelOutside ? false : true,
                      offset: !!isLabelOutside ? labelOffset : 0,
                      color: !!isLabelOutside ? "white" : "#000",
                      font: { weight: "bold", size: labelSize },
                      textAlign: isLabelOutside ? "center" : undefined,
                      padding: isLabelOutside
                        ? { top: 5, bottom: 5 }
                        : undefined,
                      backgroundColor: isLabelOutside
                        ? pieData?.datasets[0]?.borderColor
                        : undefined,
                      borderColor: isLabelOutside
                        ? "white" //chartData?.datasets[0]?.backgroundColor //"#001f87"
                        : undefined,
                      borderWidth: 1,
                      borderRadius: 4,
                      formatter: (value, context) => {
                        if (!!showPercentage) {
                          const total =
                            context.chart.data.datasets[0].data.reduce(
                              (acc, val) => acc + val,
                              0
                            );
                          const percentage = ((value / total) * 100).toFixed(1);

                          return `${formatChartValue(value, {
                            isForMoney: isForMoney || showPercentage,
                            useShortNumber,
                          })}\n(${percentage}%)`;
                        } else {
                          return `${formatChartValue(value, {
                            isForMoney,
                            useShortNumber,
                          })}`;
                        }
                      },
                      transitions: {
                        active: {
                          animation: {
                            duration: 400,
                            easing: "easeOutBounce",
                          },
                        },
                      },
                    }
                  : false,
              },
              elements: {
                arc: {
                  borderWidth: 1,
                  hoverBorderWidth: 3,
                  hoverOffset: 15,
                },
              },
              animation: {
                animateRotate: true,
                animateScale: true,
                duration: 1200,
                easing: "easeOutQuart",
              },
            }}
            data={{
              ...pieData,
              datasets: pieData.datasets.map((ds) => ({
                ...ds,
                offset: (ctx) => (ctx.active ? 20 : 0),
              })),
            }}
            plugins={enableDataLabels ? [ChartDataLabels] : []}
          />
        </div>
      ) : !loading ? (
        <Alert severity="warning">
          <AlertTitle>Warning</AlertTitle>
          No Data Provided!
        </Alert>
      ) : (
        <SkeletonPieChart title={pieTitle} />
      )}
    </>
  );
};

// Custom Plugin to Display Total in Center
const centerTextPlugin = (displayType) => ({
  id: "centerText",
  beforeDraw: (chart) => {
    const { width, height, ctx } = chart;
    ctx.restore();

    const data = chart?.data?.datasets[0]?.data || [];
    const total = data.reduce((acc, value) => acc + parseInt(value), 0);
    let centerText = "";

    if (displayType === "total" && !!data[0]) {
      centerText = `Total: ${total.toFixed(0)}`;
    } else if (displayType === "percentage" && !!data[0]) {
      const percent = ((+data[0] / total) * 100).toFixed(1);
      centerText = `${percent}%`;
    }

    ctx.font = "bold 16px Arial";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillStyle = "#333";
    ctx.fillText(centerText, width / 2, height / 2);
    ctx.save();
  },
});

export const DashboardDoughnutChart = ({
  chartTitle = "Employee years",
  chartData,
  loading = false,
  centerDisplay = "none", // ✅ "none" | "total" | "percentage"
  enableDataLabels = false,
  labelSize = 10,
  isForMoney = false,
  useShortNumber = false,
  showPercentage = false,
  labelPosition = "inside",
  labelOffset=10,
}) => {
  const isLabelOutside = labelPosition === "outside" || !!showPercentage;
  
  return (
    <>
      {!loading && !!chartData?.labels ? (
        <div
          className="border rounded p-2 dashboard-card"
          role="region"
          aria-label={`Doughnut chart: ${chartTitle}`}
        >
          <Doughnut
            role="img"
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  position: "bottom",
                },
                title: {
                  display: true,
                  text: chartTitle,
                },
                tooltip: {
                  enabled: true,
                },
                datalabels: enableDataLabels
                  ? {
                      display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
                      anchor: !isLabelOutside ? "center" : "end",
                      // align: !isLabelOutside ? "center" : "end",
                      clamp: labelPosition === "auto" ? true : true,
                      // clip: !isLabelOutside ? false : true,
                      offset: !!isLabelOutside ? labelOffset : 0,
                      color: !!isLabelOutside ? "white" : "#000",
                      font: {
                        weight: "bold",
                        size: labelSize,
                      },
                      textAlign: isLabelOutside ? "center" : undefined,
                      padding: isLabelOutside
                        ? { top: 5, bottom: 5 }
                        : undefined,
                      backgroundColor: isLabelOutside
                        ? chartData?.datasets[0]?.borderColor
                        : //"rgba(255,255,255,0.8)"
                          undefined,
                      borderColor: isLabelOutside
                        ? "white" //chartData?.datasets[0]?.backgroundColor //"#001f87"
                        : undefined,
                      borderWidth: isLabelOutside ? 1 : undefined,
                      borderRadius: isLabelOutside ? 4 : undefined,
                      formatter: (value, context) => {
                        if (!!showPercentage) {
                          const total =
                            context.chart.data.datasets[0].data.reduce(
                              (acc, val) => acc + val,
                              0
                            );
                          const percentage = ((value / total) * 100).toFixed(1);

                          return `${formatChartValue(value, {
                            isForMoney,
                            useShortNumber: useShortNumber || showPercentage,
                          })}\n(${percentage}%)`;
                        } else {
                          return `${formatChartValue(value, {
                            isForMoney,
                            useShortNumber,
                          })}`;
                        }
                      },
                      transitions: {
                        active: {
                          animation: {
                            duration: 400,
                            easing: "easeOutBounce",
                          },
                        },
                      },
                    }
                  : false,
              },
              elements: {
                arc: {
                  borderWidth: 1,
                  hoverBorderWidth: 3,
                  hoverOffset: 15,
                },
              },
              animation: {
                animateRotate: true,
                animateScale: true,
                duration: 1200,
                easing: "easeOutQuart",
              },
            }}
            data={chartData}
            plugins={[
              ...(centerDisplay !== "none"
                ? [centerTextPlugin(centerDisplay)]
                : []),
              ...(enableDataLabels ? [ChartDataLabels] : []),
            ]}
          />
        </div>
      ) : !loading ? (
        <Alert severity="warning">
          <AlertTitle>Warning</AlertTitle>
          No Data Provided!
        </Alert>
      ) : (
        <SkeletonPieChart title={chartTitle} />
      )}
    </>
  );
};

export const DashboardLineChart = ({
  chartTitle = "Line chart variations",
  chartData,
  loading = false,
  ignoreBorder = false,
  isForMoney = false,
  useShortNumber = false,
  enableDataLabels = true,
  legendPosition = "bottom",
}) => {
  return (
    <div
      className={`${!ignoreBorder ? "border rounded p-2" : ""} dashboard-card`}
    >
      {!loading && !!chartData?.labels ? (
        <Line
          redraw={true}
          options={{
            responsive: true,
            plugins: {
              legend: {
                position: legendPosition,
              },
              title: {
                display: true,
                text: chartTitle,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.dataset.label || "";
                    const value = context.raw;
                    return `${label}: ${formatChartValue(value, {
                      isForMoney,
                      useShortNumber,
                    })}`;
                  },
                },
              },
              datalabels: enableDataLabels
                ? {
                    display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
                    color: "#000",
                    anchor: "end",
                    align: "top",
                    font: {
                      weight: "bold",
                      size: 11,
                    },
                    formatter: (value) =>
                      formatChartValue(value, { isForMoney, useShortNumber }),
                    padding: { top: 16, bottom: 0 },
                  }
                : false,
            },

            interaction: {
              mode: "index",
              intersect: false,
            },

            scales: {
              y: {
                ticks: {
                  callback: (value) =>
                    formatChartValue(value, { isForMoney, useShortNumber }),
                },
              },
            },
          }}
          data={chartData}
        />
      ) : !loading ? (
        <Alert severity="warning">
          <AlertTitle>Warning</AlertTitle>
          No Data Provided!
        </Alert>
      ) : (
        <table>
          <TableSkeleton cols={1} rows={8} />
        </table>
      )}
    </div>
  );
};

// tooltipFormatter={(context) => {
//     const label = context.dataset.label;
//     const value = context.raw;
//     const index = context.dataIndex;

//     const planned = context.chart.data.datasets[0].data[index];
//     const taken = context.chart.data.datasets[1]?.data[index] ?? 0;
//     const diff = planned - taken;
//     const percent = planned ? ((taken / planned) * 100).toFixed(1) : 0;

//     return label.includes("Taken")
//       ? [`Taken: ${value}`, `Difference: ${diff}`, `Utilization: ${percent}%`]
//       : `${label}: ${value}`;
//   }}

export const DashboardBarChart = ({
  chartTitle,
  chartData,
  loading = false,
  isStackedChart = false,
  isHorizontalBar = false,
  ignoreBorder = false,
  redraw = false,
  enableDataLabels = false,
  isForMoney = false,
  useShortNumber = false,
  enableTooltipEnhancements = false,
  tooltipFormatter,
  enableAnnotations = false,
  annotationConfig = {},
  enableZoom = false,
}) => {
  return (
    <div
      className={`${!ignoreBorder ? "border rounded p-2" : ""} dashboard-card`}
    >
      {!loading && !!chartData?.labels ? (
        <Bar
          redraw={redraw}
          options={{
            indexAxis: isHorizontalBar ? "y" : undefined,
            elements: {
              bar: {
                borderWidth: isHorizontalBar ? 2 : undefined,
              },
            },
            responsive: true,
            plugins: {
              legend: {
                position: "bottom",
              },
              title: {
                display: chartTitle && true,
                text: chartTitle,
              },
              tooltip: enableTooltipEnhancements
                ? {
                    callbacks: {
                      label:
                        tooltipFormatter ||
                        ((context) =>
                          `${context.dataset.label}: ${formatChartValue(
                            context.raw,
                            {
                              isForMoney,
                              useShortNumber,
                            }
                          )}`),
                    },
                  }
                : undefined,
              annotation: enableAnnotations ? annotationConfig : undefined,
              zoom: enableZoom
                ? {
                    limits: {
                      y: { min: 0, max: 200, minRange: 50 },
                    },
                    pan: { enabled: true, mode: "xy" },
                    zoom: {
                      wheel: { enabled: true },
                      pinch: { enabled: true },
                      mode: "xy",
                    },
                  }
                : undefined,
              datalabels: enableDataLabels
                ? {
                    display: (context) => {
                      // Hide label if value is zero
                      return context.dataset.data[context.dataIndex] !== 0;
                    },
                    color: "#000",
                    font: {
                      weight: "bold",
                    },
                    formatter: (value) =>
                      formatChartValue(value, { isForMoney, useShortNumber }),
                  }
                : false,
            },
            scales: {
              x: {
                stacked: isStackedChart,
              },
              y: {
                stacked: isStackedChart,
                ticks: {
                  callback: (value) =>
                    formatChartValue(value, { isForMoney, useShortNumber }),
                },
              },
            },
            interaction: {
              mode: "index",
              intersect: false,
            },
          }}
          data={chartData}
          // plugins={[...(enableDataLabels ? [ChartDataLabels] : [])]}
        />
      ) : !loading ? (
        <Alert severity="warning">
          <AlertTitle>Warning</AlertTitle>
          No Data Provided!
        </Alert>
      ) : (
        <table>
          <TableSkeleton cols={1} rows={8} />
        </table>
      )}
    </div>
  );
};

export const DashboardPolarAreaChart = ({
  chartTitle,
  chartData,
  loading = false,
}) => (
  <div className="dashboard-card border rounded p-2">
    {!loading && !!chartData?.labels ? (
      <PolarArea
        data={chartData}
        options={{
          responsive: true,
          plugins: {
            legend: { position: "bottom" },
            title: { display: true, text: chartTitle },
          },
        }}
      />
    ) : !loading ? (
      <Alert severity="warning">
        <AlertTitle>Warning</AlertTitle>No Data Provided!
      </Alert>
    ) : (
      <SkeletonPieChart title={chartTitle} />
    )}
  </div>
);

export const DashboardRadarChart = ({
  chartTitle,
  chartData,
  loading = false,
}) => (
  <div className="dashboard-card border rounded p-2">
    {!loading && !!chartData?.labels ? (
      <Radar
        data={chartData}
        options={{
          responsive: true,
          plugins: {
            legend: { position: "bottom" },
            title: { display: true, text: chartTitle },
          },
          scales: {
            r: {
              angleLines: { display: true },
              suggestedMin: 0,
            },
          },
        }}
      />
    ) : !loading ? (
      <Alert severity="warning">
        <AlertTitle>Warning</AlertTitle>No Data Provided!
      </Alert>
    ) : (
      <SkeletonPieChart title={chartTitle} />
    )}
  </div>
);

export const DashboardMultiSeriesPieChart = ({
  chartTitle = "Multi-Series Pie Chart",
  chartData,
  loading = false,
  centerDisplay = "none", // ✅ "none" | "total" | "percentage"
  enableDataLabels = false,
}) => {
  const options = {
    responsive: true,
    cutout: "40%",
    animation: {
      animateRotate: true,
      animateScale: true,
    },
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: !!chartTitle,
        text: chartTitle,
      },
      datalabels: enableDataLabels
        ? {
            color: "#000",
            formatter: (val) => (val > 0 ? parseInt(val).toFixed(1) : ""),
            font: {
              weight: "bold",
            },
          }
        : false,
    },
  };

  return (
    <>
      {!loading && !!chartData?.labels ? (
        <div
          className="border rounded p-2 dashboard-card"
          role="region"
          aria-label={`Doughnut chart: ${chartTitle}`}
        >
          <Doughnut
            role="img"
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  position: "bottom",
                },
                title: {
                  display: true,
                  text: chartTitle,
                },
                datalabels: enableDataLabels
                  ? {
                      display: (context) => {
                        // Hide label if value is zero
                        const value = context.dataset.data[context.dataIndex];
                        return value !== 0;
                      },
                      color: "#000",
                      font: {
                        weight: "bold",
                      },
                      formatter: (value) => parseInt(value).toFixed(0),
                    }
                  : false,
              },
            }}
            data={chartData}
            plugins={[
              ...(centerDisplay !== "none"
                ? [centerTextPlugin(centerDisplay)]
                : []),
              ...(enableDataLabels ? [ChartDataLabels] : []),
            ]}
          />
        </div>
      ) : !loading ? (
        <Alert severity="warning">
          <AlertTitle>Warning</AlertTitle>
          No Data Provided!
        </Alert>
      ) : (
        <SkeletonPieChart title={chartTitle} />
      )}
    </>
  );
};

export const SkeletonPieChart = ({ title }) => {
  return (
    <div className="border rounded p-2 dashboard-card text-center">
      <span style={{ fontSize: "12px", fontWeight: "700" }}>{title}</span>
      <Skeleton
        variant="circular"
        width={150}
        height={150}
        className="mx-auto mt-2 mb-3"
      />
    </div>
  );
};

// Format with short units (K, M, B)
export function formatNumberShort(value) {
  if (typeof value === "string") value = parseFloat(value.replace(/,/g, ""));
  if (isNaN(value)) return value;

  if (value >= 1_000_000_000)
    return `${(parseInt(value) / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(parseInt(value) / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(parseInt(value) / 1_000).toFixed(1)}K`;
  return value.toString();
}

// Format as money (RWF by default)
export function formatMoney(value, currency) {
  if (typeof value === "string") value = parseFloat(value.replace(/,/g, ""));
  if (isNaN(value)) return value;
  return value.toLocaleString(
    "en-US",
    currency ? { style: "currency", currency } : undefined
  );
}

// Smart formatter
export function formatChartValue(
  value,
  { useShortNumber = false, isForMoney = false } = {}
) {
  if (!value || value === 0) return "";
  if (useShortNumber) return formatNumberShort(value);
  if (isForMoney) return formatMoney(value);
  return parseInt(value)?.toFixed(0);
}
